import { getBudgetAmountInRange, hasBudgetAmountChangeBy } from '../../utils/budgetAmounts';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Divider,
  Button,
  Avatar,
  Stack,
  useTheme,
  styled
} from '@mui/material';
import { alpha, darken, getLuminance } from '@mui/material/styles';
// Removed Launch / Refresh icons per design update
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SavingsIcon from '@mui/icons-material/Savings';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import BudgetTransactionsDialog from './BudgetTransactionsDialog';
import ForecastFigure, { formatForecastPct } from './ForecastFigure';
import { forecastCategory } from '../../utils/categoryForecast';

/**
 * Lightweight monthly budget progress summary card for Analysis (monthly view)
 * - Always reflects the selected analysis month via supplied dateRange
 * - Shows aggregate Income / Expense progress + top budgets list
 */
const BudgetProgressCard = ({ selectedMonth, dateRange, monthSummary = null }) => {
  const theme = useTheme();
  const navigate = useNavigate(); // kept in case navigation re‑added later
  const { budgets, categories, getBudgetStatus, transactions, actions, updateCategory } = useData();
  const [refreshKey, setRefreshKey] = useState(0); // force recalculation if needed
  
  // Dialog state for transactions
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    categoryId: null,
    aggregateType: null,
    title: ''
  });

  const activeBudgets = useMemo(() => budgets.filter(b => b.isActive), [budgets]);

  // Compute the monthly allocated amount for a budget in the selected month
  // Duplicated from BudgetMatrixView.computeMonthlyAmount to ensure consistency
  const computeMonthlyAmount = (budget, year, monthIndex) => {
    if (hasBudgetAmountChangeBy(budget, new Date(year, monthIndex + 1, 0))) return getBudgetAmountInRange(budget, new Date(year, monthIndex, 1), new Date(year, monthIndex + 1, 0));
    const start = new Date(budget.startDate);
    const end = budget.endDate ? new Date(budget.endDate) : new Date(2099, 11, 31);
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = new Date(year, monthIndex + 1, 0);
    if (end < monthStart || start > monthEnd) return 0; // no overlap

    const msPerDay = 1000 * 60 * 60 * 24;
    const overlapStart = start > monthStart ? start : monthStart;
    const overlapEnd = end < monthEnd ? end : monthEnd;
    const overlapDays = Math.max(0, Math.round((overlapEnd - overlapStart) / msPerDay) + 1);
    const daysInMonth = Math.round((monthEnd - monthStart) / msPerDay) + 1;

    if (budget.isRecurring) {
      if (!budget.isProrated) {
        switch (budget.recurringPeriod) {
          case 'monthly':
            return Math.abs(budget.amount);
          case 'weekly': {
            const targetDow = start.getDay();
            let count = 0;
            for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
              if (d >= start && d <= end && d.getDay() === targetDow) count++;
            }
            return Math.abs(count * budget.amount);
          }
          case 'quarterly': {
            const startMonth = start.getMonth();
            const currentMonth = monthStart.getMonth();
            const diff = (monthStart.getFullYear() - start.getFullYear()) * 12 + (currentMonth - startMonth);
            if (diff >= 0 && diff % 3 === 0) return Math.abs(budget.amount);
            return 0;
          }
          case 'yearly': {
            return start.getMonth() === monthStart.getMonth() ? Math.abs(budget.amount) : 0;
          }
          default:
            return Math.abs(budget.amount);
        }
      } else {
        let cycleDays;
        switch (budget.recurringPeriod) {
          case 'weekly':
            cycleDays = 7; break;
          case 'monthly':
            return Math.abs(budget.amount) * (overlapDays / daysInMonth);
          case 'quarterly':
            cycleDays = 91.25; break;
          case 'yearly':
            cycleDays = 365; break;
          default:
            cycleDays = daysInMonth;
        }
        if (budget.recurringPeriod === 'weekly') {
          return Math.abs(budget.amount / 7) * overlapDays;
        }
        if (budget.recurringPeriod === 'quarterly') {
          return Math.abs(budget.amount) * (overlapDays / cycleDays);
        }
        if (budget.recurringPeriod === 'yearly') {
          return Math.abs(budget.amount) * (overlapDays / cycleDays);
        }
        return Math.abs(budget.amount) * (overlapDays / daysInMonth);
      }
    }
    if (overlapDays <= 0) return 0;
    return Math.abs(budget.amount) * (overlapDays / daysInMonth);
  };

  // Month label intentionally removed per request (kept logic commented if needed later)
  // const monthLabel = useMemo(() => {
  //   if (!selectedMonth) return '';
  //   const [m, y] = selectedMonth.split('-');
  //   const fullNames = {Jan:'January',Feb:'February',Mar:'March',Apr:'April',May:'May',Jun:'June',Jul:'July',Aug:'August',Sep:'September',Oct:'October',Nov:'November',Dec:'December'};
  //   return `${fullNames[m]} 20${y}`;
  // }, [selectedMonth]);

  // Compute per-budget monthly amounts for the selected month (matrix-consistent)
  const monthBudgetEntries = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return [];
    const start = new Date(dateRange.startDate);
    const year = start.getFullYear();
    const monthIndex = start.getMonth();
    return activeBudgets.map(b => {
      const monthlyAmount = computeMonthlyAmount(b, year, monthIndex);
      return { budget: b, monthlyAmount };
    }).filter(e => e.monthlyAmount > 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBudgets, dateRange?.startDate, dateRange?.endDate, refreshKey]);

  // Aggregate income & expense
  const aggregates = useMemo(() => {
    const agg = { income: { target:0, actual:0 }, expense:{ target:0, actual:0 } };
    
    // Calculate targets using matrix-consistent monthly amounts
    monthBudgetEntries.forEach(e => {
      if (e.budget.type === 'Income') agg.income.target += e.monthlyAmount;
      else agg.expense.target += e.monthlyAmount;
    });
    
    // Calculate actuals from transactions (to match Dashboard calculation)
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      const filteredTransactions = transactions.filter(txn => {
        const txnDate = new Date(txn.date);
        const category = categories.find(c => c.id === txn.category);
        return txnDate >= dateRange.startDate && 
               txnDate <= dateRange.endDate &&
               (!category || !category.isTransfer); // Exclude transfer categories
      });
      
        // Income: all positive transactions
        agg.income.actual = filteredTransactions
          .filter(t => t.amount > 0)
          .reduce((sum, t) => sum + t.amount, 0);
      
        // Expenses: all negative transactions (using absolute value)
        agg.expense.actual = filteredTransactions
          .filter(t => t.amount < 0)
          .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      }

      // If a monthSummary is provided (from BudgetView) prefer those totals to ensure
      // the BudgetProgressCard displays the exact same Income/Expenses as the header cards.
      if (monthSummary && typeof monthSummary === 'object') {
        // monthSummary stores income and expenses as numbers; when present override actuals
        agg.income.actual = typeof monthSummary.income === 'number' ? monthSummary.income : agg.income.actual;
        agg.expense.actual = typeof monthSummary.expenses === 'number' ? monthSummary.expenses : agg.expense.actual;
      }
    
  return agg;
  }, [monthBudgetEntries, transactions, dateRange, categories]);

  const formatCurrency = (amt) => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',maximumFractionDigits:0}).format(amt || 0);
  const pct = (a,b) => b > 0 ? Math.round((a/b)*100) : 0;

  // Calendar pacing (elapsed days vs total days) for expected progress markers
  const daysMeta = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return { daysInMonth: 0, daysElapsed: 0, ratio: 0 };
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    const today = new Date();
    const isCurrentMonth = start.getMonth() === today.getMonth() && start.getFullYear() === today.getFullYear();
    const daysInMonth = Math.max(1, Math.round((end - start) / (1000*60*60*24)) + 1);
    if (!isCurrentMonth) {
      // For any non-current month (past or future) treat pacing as full month complete
      return { daysInMonth, daysElapsed: daysInMonth, ratio: 1 };
    }
    // Current month: use today's date capped at end
    const rawElapsed = Math.round((Math.min(today, end) - start) / (1000*60*60*24)) + 1; // inclusive
    const daysElapsed = rawElapsed < 1 ? 1 : rawElapsed > daysInMonth ? daysInMonth : rawElapsed;
    const ratio = daysInMonth ? daysElapsed / daysInMonth : 0;
    return { daysInMonth, daysElapsed, ratio };
  }, [dateRange?.startDate, dateRange?.endDate]);

  // Determine if selected month is in the past relative to today (completed month before current month)
  const isPastMonth = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return false;
    const start = new Date(dateRange.startDate);
    const today = new Date();
    // Past month if year/month less than current year/month
    return (start.getFullYear() < today.getFullYear()) || (start.getFullYear() === today.getFullYear() && start.getMonth() < today.getMonth());
  }, [dateRange?.startDate, dateRange?.endDate]);

  // Aggregate budgets into categories with forecast projection for month end
  // Include categories that have no budgets (amount = 0) so they appear in Budget Progress
  const enrichedBudgets = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return [];
    const { ratio } = daysMeta;
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);

    // Group budgets per category to sum targets using matrix-consistent monthly amounts
    // Start by seeding all known categories (excluding transfer categories) so those without budgets show up with amount 0
    const categoryAgg = new Map();
    categories.forEach(c => {
      if (c.isTransfer) return; // ignore transfer categories
      categoryAgg.set(c.id, {
        id: c.id,
        name: c.name,
        categoryId: c.id,
        type: c.type,
        amount: 0,
        categoryColor: c.color,
        forecastMethod: c.forecastMethod,
        order: c.order,
        isPinned: !!c.isPinned
      });
    });

    // Add budgets' monthly amounts into the seeded map; include 'uncategorized' key for budgets without category
    monthBudgetEntries.forEach(e => {
      const b = e.budget;
      const key = b.categoryId || 'uncategorized';
      const category = categories.find(c => c.id === b.categoryId);
      if (!categoryAgg.has(key)) {
        categoryAgg.set(key, {
          id: key,
          name: category?.name || b.name || 'Unnamed',
          categoryId: b.categoryId,
          type: b.type,
          amount: 0,
          categoryColor: category?.color,
          forecastMethod: category?.forecastMethod,
          order: category?.order,
          isPinned: !!category?.isPinned
        });
      }
      categoryAgg.get(key).amount += e.monthlyAmount; // sum targets
    });

    // Compute spent once per category from transactions to prevent double counting when multiple budgets share category
    const results = Array.from(categoryAgg.values()).map(cat => {
      const catSpent = transactions
        .filter(txn => txn.category === cat.categoryId && new Date(txn.date) >= start && new Date(txn.date) <= end)
        .filter(txn => (cat.type === 'Income' ? txn.amount > 0 : txn.amount < 0))
        .reduce((sum, txn) => sum + Math.abs(txn.amount), 0);
      const spent = catSpent;

      // Forecast = spent + committed action occurrences + estimated remainder,
      // using the category's chosen method (utils/categoryForecast.js) — the
      // pace/history blend by default. Completed months come back as actuals
      // (isPast).
      const forecastTerms = forecastCategory({
        categoryId: cat.categoryId,
        type: cat.type,
        periodStart: start,
        periodEnd: end,
        transactions,
        actions,
        method: cat.forecastMethod,
      });
      const projected = forecastTerms ? forecastTerms.forecast : spent;
      const percentage = cat.amount > 0 ? (spent / cat.amount) * 100 : 0;
      const projectedPct = cat.amount > 0 ? (projected / cat.amount) * 100 : 0;
      const expectedSoFar = cat.amount * ratio;
      const expectedPct = cat.amount > 0 ? (expectedSoFar / cat.amount) * 100 : 0;
      return {
        ...cat,
        spent,
        percentage,
        projected,
        projectedPct,
        forecastTerms,
        expectedSoFar,
        expectedPct,
        remaining: cat.amount - spent,
        // Allow 1% leeway before flagging over on forecast for expenses
        isOver: projectedPct >= 101 && cat.type === 'Expense'
      };
    });

    // Pinned categories first, then the Categories page ordering (protected
    // first, then drag order, then name) so this list reads the same as
    // Categories.
    return results.sort((a, b) => {
      if (!!a.isPinned !== !!b.isPinned) return a.isPinned ? -1 : 1;
      const aProtected = ['income', 'expense'].includes(a.id);
      const bProtected = ['income', 'expense'].includes(b.id);
      if (aProtected !== bProtected) return aProtected ? -1 : 1;
      if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [monthBudgetEntries, categories, dateRange, daysMeta, transactions, actions]);

  // Aggregated projections & expected pacing
  const aggregateForecast = useMemo(()=>{
    let incomeProjected=0, expenseProjected=0, incomeExpected=0, expenseExpected=0;
    enrichedBudgets.forEach(b=>{
      if (b.type === 'Income') { incomeProjected += b.projected; incomeExpected += b.expectedSoFar; }
      else { expenseProjected += b.projected; expenseExpected += b.expectedSoFar; }
    });
    return { incomeProjected, expenseProjected, incomeExpected, expenseExpected };
  }, [enrichedBudgets]);

  // Icon + verbose tooltip logic derived from pacing (expected progress based on today's date)
  const statusInfo = (b) => {
    const actual = b.percentage;
    const expected = b.expectedPct;
  const forecast = b.projectedPct;
    let icon, message;
    if (isPastMonth) {
      // Past month: only final outcome, no pacing warnings
      if (b.type === 'Expense') {
        if (actual >= 99 && actual <= 101) {
          icon = <CheckCircleOutlineIcon fontSize="small" color="success" />;
          message = `Budget met (Final ${actual.toFixed(1)}% of target).`;
        } else if (actual > 101) {
          icon = <CancelIcon fontSize="small" color="error" />;
          message = `Over budget (Final ${actual.toFixed(1)}% of target).`;
        } else {
          icon = <CheckCircleOutlineIcon fontSize="small" color="success" />;
          message = `Under budget (Final ${actual.toFixed(1)}% of target).`;
        }
      } else { // Income
        if (actual >= 99 && actual <= 101) {
          icon = <CheckCircleOutlineIcon fontSize="small" color="success" />;
          message = `Income target met (Final ${actual.toFixed(1)}%).`;
        } else if (actual > 101) {
          icon = <CancelIcon fontSize="small" color="error" />;
          message = `Income target exceeded (Final ${actual.toFixed(1)}% of target).`;
        } else {
          icon = <CancelIcon fontSize="small" color="error" />;
          message = `Income target missed (Final ${actual.toFixed(1)}% of target).`;
        }
      }
      return { icon, message };
    }
    // Current/future month pacing logic
    if (b.type === 'Expense') {
      if (actual > 101) {
        icon = <CancelIcon fontSize="small" color="error" />;
        message = `Over budget (Actual ${actual.toFixed(1)}% > 101%).`;
      } else if (forecast >= 99 && forecast <= 101) {
        // Within leeway, treat as on track
        icon = <CheckCircleOutlineIcon fontSize="small" color="success" />;
        message = `On track (Forecast ${forecast.toFixed(1)}%${expected ? `, expected ${expected.toFixed(1)}%` : ''}).`;
      } else if (forecast > 101) {
        // Projected to exceed target beyond 1% leeway
        const over = (forecast - 100).toFixed(1);
        icon = <WarningAmberIcon fontSize="small" color="warning" />;
        message = `Projected to exceed by ${over} pts (Forecast ${forecast.toFixed(1)}%).`;
      } else {
        icon = <CheckCircleOutlineIcon fontSize="small" color="success" />;
        message = `On track (Forecast ${forecast.toFixed(1)}%${expected ? `, expected ${expected.toFixed(1)}%` : ''}).`;
      }
    } else { // Income
      if (actual < expected) {
        const behind = (expected - actual).toFixed(1);
        icon = <WarningAmberIcon fontSize="small" color="warning" />;
        message = `Behind pace by ${behind} pts (Actual ${actual.toFixed(1)}% vs expected ${expected.toFixed(1)}%).`;
      } else {
        icon = <CheckCircleOutlineIcon fontSize="small" color="success" />;
        message = actual >= 100
          ? `Target achieved (Actual ${actual.toFixed(1)}%).`
          : `On / ahead of pace (Actual ${actual.toFixed(1)}% vs expected ${expected.toFixed(1)}%).`;
      }
    }
    return { icon, message };
  };

  // Category bar colors: fixed mapping (Expense = red, Income = green) per request
  const linearColor = (b) => b.type === 'Expense' ? 'error' : 'success'; // still used for aggregate bars

  const categoryBarStyle = (b) => {
    const raw = b.categoryColor || (b.type === 'Income' ? theme.palette.success.main : theme.palette.error.main);
    // Pastel category colors are nearly invisible against the light track;
    // darken anything too light so the fill stays readable.
    let base = raw;
    try {
      if (getLuminance(raw) > 0.6) base = darken(raw, 0.25);
    } catch {
      base = theme.palette.grey[500];
    }
    const track = alpha(base, 0.15);
    return {
      height: 8,
      borderRadius: 3,
      backgroundColor: track,
      '& .MuiLinearProgress-bar': {
        backgroundColor: base
      }
    };
  };

  const refresh = () => setRefreshKey(k=>k+1); // retained (currently unused) in case refresh control restored

  // Click handlers for showing transactions
  const handleShowIncomeTransactions = () => {
    setDialogConfig({
      categoryId: null,
      aggregateType: 'Income',
      title: 'Income Transactions'
    });
    setTransactionDialogOpen(true);
  };

  const handleShowExpenseTransactions = () => {
    setDialogConfig({
      categoryId: null,
      aggregateType: 'Expense',
      title: 'Expense Transactions'
    });
    setTransactionDialogOpen(true);
  };

  const handleShowCategoryTransactions = (categoryId, categoryName) => {
    setDialogConfig({
      categoryId: categoryId,
      aggregateType: null,
      title: `${categoryName} Transactions`
    });
    setTransactionDialogOpen(true);
  };

  // Persist a category's forecast method (picked from the forecast popover).
  // updateCategory's optimistic categories update recomputes enrichedBudgets.
  const handleForecastMethodChange = (categoryId, method) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;
    updateCategory({ ...category, forecastMethod: method, originalId: category.id })
      .catch(() => { /* updateCategory already logs and rolls back */ });
  };

  // Pin/unpin a category to the top of this list.
  const handleTogglePin = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;
    updateCategory({ ...category, isPinned: !category.isPinned, originalId: category.id })
      .catch(() => { /* updateCategory already logs and rolls back */ });
  };

  const handleCloseTransactionDialog = () => {
    setTransactionDialogOpen(false);
    setDialogConfig({
      categoryId: null,
      aggregateType: null,
      title: ''
    });
  };

  if (!selectedMonth) return null;

  // Neutral category label (replaces colored chips)
  const CategoryLabel = styled(Box)(({ theme }) => ({
  padding: 0,
  // Match Income/Expenses header sizing
  fontSize: '0.875rem',
  fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'color 0.2s ease'
  }));

  // Removed gradient header bar; using standard CardContent + Divider like chart sections (Income ➡ Expenses)

  return (
    <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: '1.1rem' } }}>
            <SavingsIcon />
          </Box>
          Budget Progress
        </Typography>
      </Box>
      <Divider />
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
  {enrichedBudgets.length === 0 && (
          <Typography variant="body2" color="text.secondary">No active budgets or categories found for this month.</Typography>
        )}
  {enrichedBudgets.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={1.5} alignItems="stretch">
              <Box sx={{ flex: 1 }}>
                <AggregateProgress
                  label="Income"
                  icon={<ArrowUpwardIcon />}
                  actual={aggregates.income.actual}
                  target={aggregates.income.target}
                  projected={aggregateForecast.incomeProjected}
                  expected={aggregateForecast.incomeExpected}
                  daysRatio={daysMeta.ratio}
                  color="success"
                  isPastMonth={isPastMonth}
                  onClick={handleShowIncomeTransactions}
                />
              </Box>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Box sx={{ flex: 1 }}>
                <AggregateProgress
                  label="Expenses"
                  icon={<ArrowDownwardIcon />}
                  actual={aggregates.expense.actual}
                  target={aggregates.expense.target}
                  projected={aggregateForecast.expenseProjected}
                  expected={aggregateForecast.expenseExpected}
                  daysRatio={daysMeta.ratio}
                  color="error"
                  isPastMonth={isPastMonth}
                  onClick={handleShowExpenseTransactions}
                />
              </Box>
            </Stack>
            <Divider sx={{ my: 1 }} />
            {/* Two-column row grid on desktop: shorter bars are easier to compare */}
            {/* Phones scroll the page; the inner scroll region is desktop-only */}
            <Box sx={{ flex: 1, overflowY: { xs: 'visible', sm: 'auto' }, pr: 0.5, maxHeight: { xs: 'none', sm: 10 * 60 + 'px' } }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  columnGap: 3,
                  rowGap: 0.8
                }}
              >
                {enrichedBudgets.map(b => (
                  <Box
                      key={b.id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25,
                        fontSize: '0.8125rem',
                        cursor: 'pointer',
                        p: 0.75,
                        borderRadius: 1,
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          '& .category-label': {
                            color: 'primary.main'
                          },
                          '& .pin-button': {
                            opacity: 1
                          }
                        }
                      }}
                      onClick={() => handleShowCategoryTransactions(b.categoryId, b.name)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, minWidth: 0 }}>
                        <CategoryLabel className="category-label" title={b.name}>{b.name}</CategoryLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.25 }}>
                          {b.categoryId && categories.some(c => c.id === b.categoryId) && (
                            <IconButton
                              className="pin-button"
                              size="small"
                              title={b.isPinned ? 'Unpin from top' : 'Pin to top'}
                              onClick={(e) => { e.stopPropagation(); handleTogglePin(b.categoryId); }}
                              sx={{ p: 0.25, opacity: b.isPinned ? 1 : { xs: 0.4, sm: 0 }, transition: 'opacity 0.15s ease' }}
                            >
                              {b.isPinned
                                ? <PushPinIcon sx={{ fontSize: '0.95rem', color: 'primary.main' }} />
                                : <PushPinOutlinedIcon sx={{ fontSize: '0.95rem', color: 'text.secondary' }} />}
                            </IconButton>
                          )}
                          {statusInfo(b).icon}
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, b.percentage)}
                        sx={categoryBarStyle(b)}
                      />
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'flex-end' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 0.1, sm: 0.5 }
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{formatCurrency(b.spent)}</Box>
                          {' of '}{formatCurrency(b.amount)}
                          {' · '}
                          {b.type === 'Income'
                            ? (b.remaining > 0 ? `${formatCurrency(b.remaining)} to go` : `${formatCurrency(Math.abs(b.remaining))} ahead`)
                            : (b.remaining >= 0 ? `${formatCurrency(b.remaining)} left` : `${formatCurrency(Math.abs(b.remaining))} over`)}
                        </Typography>
                        {b.forecastTerms ? (
                          <ForecastFigure
                            terms={b.forecastTerms}
                            budgetAmount={b.amount}
                            type={b.type}
                            onMethodChange={
                              b.categoryId && categories.some(c => c.id === b.categoryId)
                                ? (method) => handleForecastMethodChange(b.categoryId, method)
                                : undefined
                            }
                            sx={{ ml: { xs: 0, sm: 'auto' } }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary', ml: { xs: 0, sm: 'auto' } }}>
                            Fcast: {formatCurrency(b.projected)} ({formatForecastPct(b.projectedPct)})
                          </Typography>
                        )}
                      </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          {/* ...existing code... */}
      {/* Info note at the bottom */}
      {enrichedBudgets.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', px: 2, pb: 1, pt: 0.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary', mr: 0.7 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Forecast = spent so far + scheduled actions still to land + an estimated remainder (tap a forecast for its terms and to choose how that category's remainder is estimated). Past months show actuals. Click items to view transactions.
          </Typography>
        </Box>
      )}
          </Box>
        )}
      </CardContent>
      
      {/* Transaction Details Dialog */}
      <BudgetTransactionsDialog
        open={transactionDialogOpen}
        onClose={handleCloseTransactionDialog}
        categoryId={dialogConfig.categoryId}
        aggregateType={dialogConfig.aggregateType}
        dateRange={dateRange}
        title={dialogConfig.title}
      />
    </Card>
  );
};

const AggregateProgress = ({ label, icon, actual, target, projected, expected, daysRatio, color, isPastMonth, onClick }) => {
  const theme = useTheme();
  const iconMain = theme.palette[color]?.main || theme.palette.primary.main;
  const formatCurrency = (amt) => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',maximumFractionDigits:0}).format(amt || 0);
  const pct = target > 0 ? Math.round((actual/target)*100) : 0;
  const projectedPct = target > 0 ? Math.round((projected/target)*100) : 0;
  const expectedPct = target > 0 ? Math.round((expected/target)*100) : 0;
  const remaining = target - actual;
  // Derive icon + message using same pacing logic as categories
  const status = (() => {
    const type = label === 'Income' ? 'Income' : 'Expense';
    const actualPct = pct;
    const expPct = expectedPct;
  if (isPastMonth) {
      if (type === 'Expense') {
        return actualPct >= 100
          ? { icon: <CancelIcon fontSize="small" color="error" />, msg: `Over budget (Final ${actualPct}% of target).` }
          : { icon: <CheckCircleOutlineIcon fontSize="small" color="success" />, msg: `Under budget (Final ${actualPct}% of target).` };
      } else {
        return actualPct >= 100
          ? { icon: <CheckCircleOutlineIcon fontSize="small" color="success" />, msg: `Income target met (Final ${actualPct}%).` }
          : { icon: <CancelIcon fontSize="small" color="error" />, msg: `Income target missed (Final ${actualPct}% of target).` };
      }
    }
    if (type === 'Expense') {
      if (actualPct >= 100) return { icon: <CancelIcon fontSize="small" color="error" />, msg: `Over budget (Actual ${actualPct}% ≥ 100%).` };
      if (actualPct > expPct) return { icon: <WarningAmberIcon fontSize="small" color="warning" />, msg: `Above pace by ${(actualPct - expPct).toFixed(1)} pts (Actual ${actualPct}% vs expected ${expPct}%).` };
      return { icon: <CheckCircleOutlineIcon fontSize="small" color="success" />, msg: `On or under pace (Actual ${actualPct}% vs expected ${expPct}%).` };
    } else {
      if (actualPct < expPct) return { icon: <WarningAmberIcon fontSize="small" color="warning" />, msg: `Behind pace by ${(expPct - actualPct).toFixed(1)} pts (Actual ${actualPct}% vs expected ${expPct}%).` };
      return { icon: <CheckCircleOutlineIcon fontSize="small" color="success" />, msg: actualPct >= 100 ? `Target achieved (Actual ${actualPct}%).` : `On / ahead of pace (Actual ${actualPct}% vs expected ${expPct}%).` };
    }
  })();
  return (
    <Box 
      sx={{ 
        flex: 1, 
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
        p: onClick ? 1 : 0,
        borderRadius: 1,
        transition: 'background-color 0.2s ease',
        '&:hover': onClick ? {
          backgroundColor: 'action.hover'
        } : {}
      }}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, alignItems:'baseline', flexWrap:'wrap', gap:0.5 }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 600, 
            fontSize: '0.875rem',
            transition: 'color 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            letterSpacing: 0.01,
            ...(onClick && {
              '&:hover': {
                color: 'primary.main'
              }
            })
          }}
        >
          <Box
            component="span"
            sx={{
              width: 24,
              height: 24,
              // Rounded square (not a circle) to match the MetricCard avatars
              // above — same corner-to-size ratio as their 50px/16px shape.
              borderRadius: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: alpha(iconMain, 0.12),
              color: iconMain,
              '& svg': { fontSize: 16 },
            }}
          >
            {icon}
          </Box>
          {label}
        </Typography>
      </Box>
  <Box sx={{ position:'relative', mb: 0.3, pr:4 }}>
        <LinearProgress variant="determinate" value={Math.min(100,pct)} color={color} sx={{ height: 12, borderRadius: 6 }} />
        <Box sx={{ position:'absolute', right:4, top:'50%', transform:'translateY(-50%)', display:'flex', alignItems:'center' }}>
          {status.icon}
        </Box>
      </Box>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.2
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{formatCurrency(actual)}</Box>
          {' of '}{formatCurrency(target)} ({pct}%)
          {' · '}
          {label === 'Income'
            ? (remaining > 0 ? `${formatCurrency(remaining)} to go` : `${formatCurrency(Math.abs(remaining))} ahead`)
            : (remaining >= 0 ? `${formatCurrency(remaining)} left` : `${formatCurrency(Math.abs(remaining))} over`)}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.8125rem', fontWeight: 500, color: (projectedPct > 101 && label === 'Expenses') ? 'error.main' : ((projectedPct >= 100 && label === 'Income') ? 'success.main' : 'text.secondary'), textAlign: 'left' }}>
          Fcast: {formatCurrency(projected)} ({formatForecastPct(projectedPct)})
        </Typography>
      </Box>
    </Box>
  );
};

export default BudgetProgressCard;

